import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useImpersonation } from '@/hooks/useImpersonation';
import { Users, UserX } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function UserImpersonation() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const { impersonatedUser, isImpersonating, startImpersonation, stopImpersonation } = useImpersonation();

  const { data: users, isLoading } = useQuery({
    queryKey: ['all-users', searchTerm],
    queryFn: async () => {
      let query = supabase
        .from('profiles')
        .select('id, email, first_name, last_name, district_id')
        .order('email');

      if (searchTerm) {
        query = query.or(`email.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query.limit(50);
      if (error) throw error;
      return data;
    },
  });

  const handleImpersonate = async () => {
    if (selectedUserId) {
      await startImpersonation(selectedUserId);
    }
  };

  if (isImpersonating) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Currently Impersonating
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>
              You are currently viewing the application as: <strong>{impersonatedUser?.full_name || impersonatedUser?.email}</strong>
            </AlertDescription>
          </Alert>
          <Button onClick={stopImpersonation} variant="destructive" className="w-full">
            <UserX className="h-4 w-4 mr-2" />
            Stop Impersonation
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          User Impersonation
        </CardTitle>
        <CardDescription>
          Impersonate any user to see their view and troubleshoot issues
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Search Users</label>
          <Input
            placeholder="Search by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Select User</label>
          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a user to impersonate" />
            </SelectTrigger>
            <SelectContent>
              {isLoading ? (
                <SelectItem value="loading" disabled>Loading users...</SelectItem>
              ) : users && users.length > 0 ? (
                users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.first_name && user.last_name 
                      ? `${user.first_name} ${user.last_name}` 
                      : user.email} ({user.email})
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-users" disabled>No users found</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        <Button 
          onClick={handleImpersonate} 
          disabled={!selectedUserId}
          className="w-full"
        >
          Start Impersonation
        </Button>
      </CardContent>
    </Card>
  );
}
